import type {
	ISbStory,
	ISbStoryData,
	StoryblokRichTextNode,
} from '@storyblok/js';

import type { Language, LinkTarget } from '@valk-nx/core/lib/core';
import type { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';

export interface ISbStoryBlok extends ISbStoryData<ISbComponentType<string>> {
	component: string;
}

export interface Image {
	file: string;
	alt: string;
}

export interface ImageWithoutAspectRatio {
	bynder: BynderImage[];
	imageAlt: string;
}

export interface BynderImage {
	id: string;
	name: string;
	databaseId: string;
	description: string | null;
	files: {
		mini: BynderFile;
		webImage: BynderFile;
		thumbnail: BynderFile;
		transformBaseUrl: BynderFile;
	};
}

interface BynderFile {
	url: string;
	width: number | null;
	height: number | null;
	fileSize: number | null;
}

export type ImageAspectRatio = '1:1' | '16:9' | '4:3' | '3:2' | '21:9';

export interface Carousel {
	config: {
		keyboard: boolean;
	};
	component: string;
	items: CarouselItem[];
}

export interface StoryblokImage {
	bynder: BynderImage[];
	imageAlt?: string;
}

export interface CarouselItem {
	bynder?: BynderImage[];
	video?: Video[];
	sticker: BynderImage[];
	imageAlt?: string;
	heading: string;
	subHeading: string;
	button: Button[];
}

export interface ItemSliderItem {
	bynder: BynderImage[];
	imageAlt: string;
	link: Link;
	overline: string;
	title: string;
	fragment?: string;
}

export interface Button {
	component?: string;
	size: string;
	text: string;
	type: string;
	link: Link;
}

export interface ButtonList {
	buttons: Button[];
	component?: string;
}

export interface DropdownButtonItem {
	component: string;
	icon: string;
	text: string;
	url: Link;
}

export interface DropdownButton {
	component: string;
	title: string;
	items: DropdownButtonItem[];
}

export interface Link {
	// NOTE: Storyblok uses the cached_url which contains the actual url
	cached_url: string;
	url: string;
	target?: LinkTarget;
	fieldtype?: string;
	id?: string;
	linktype?: string;
	prep?: boolean;
}

export interface LinkList {
	component?: string;
	blocks: LinkListItem[];
	showDivider: boolean;
	showIcons: boolean;
	useChevron: boolean;
}

export interface LinkListItem {
	icon: string;
	text: string;
	url: Link;
}

export interface MenuItem {
	icon?: string;
	link: Link;
	title: string;
}

export interface SbFooterMenu {
	title: string;
	items: MenuItem[];
}

export interface PageMetaData {
	metaDescription: string;
	metaTitle: string;
	robotsNoFollow: boolean;
	robotsNoIndex: boolean;
}

export interface RootInterface {
	slug: string;
	isDraft: boolean;
	language: Language;
}

export type ColumnComponentTypes = 'columnOne' | 'columnTwo' | 'columnThree';

export type ColumnComponentAlignments = 'left' | 'center';

export const isColumnComponentAlignment = (
	input: string | null | undefined,
): input is ColumnComponentAlignments => {
	return !!input && (input === 'center' || input === 'left');
};

export interface Text {
	component: string;
	text: string;
	_editable: string;
	_uid: string;
}

export interface CardImages {
	component: string;
	bynder: BynderImage[];
	imageAlt: string;
	_editable: string;
	_uid: string;
}

export interface HotelByDealCardInterface {
	component: string;
	hotelName: string;
	hotelSlug: string;
	image: CardImages[];
	ribbonColor: string;
	ribbonText: string;
	usps: Text[];
	price: string;
	tags: Text[];
	rating: string;
	numberOfRatings: string;
	numberOfStars: string;
	ctaUrl: Link;
	validFrom: string;
	validUntil: string;
	currency?: string;
}

export interface CardInterface {
	component: string;
	content: StoryblokRichTextNode<string>;
	heading: HeadingGroupSection[];
	images: CardImages[];
	isHorizontal: boolean;
	footer: Button[] | ButtonList;
	tags: string[];
}

export type FormInput =
	| 'text'
	| 'tel'
	| 'email'
	| 'textarea'
	| 'segmentedControl'
	| 'select'
	| 'checkboxGroup'
	| 'radioGroup';

export interface FormHeaderInterface {
	component: string;
}

export interface FormInputInterface {
	component: string;
	header: string;
	helperText: string;
	icon: string;
	iconPosition: 'left' | 'right';
	label: string;
	mask: string;
	maskPrefix: string;
	optional: boolean;
	placeholder: string;
	sendConfirmation?: boolean;
	tooltipContent: string;
	tooltipLabel: string;
	type: FormInput;
	_uid: string;
}

export interface FormCheckboxInterface extends FormInputInterface {
	toggle: boolean;
}

export interface FormSelectInterface extends FormInputInterface {
	options: {
		label: string;
		value: string;
	}[];
}

// Cloned from "import { ISbComponentType } from 'storyblok-js-client/src/interfaces';"
// importing gives a type errors to unrelated types
export interface ISbComponentType<T extends string> {
	_uid?: string;
	component?: T;
	_editable?: string;
	translatedSlugs?: {
		selected: boolean;
		lang: Language;
		name: string;
		path: string;
	}[];
}

type StoryblokContent = ISbComponentType<string> & object;

export interface StoryblokGlobals {
	header: undefined | (ISbComponent & { hideMenu: boolean });
	banner: Banner | undefined;
	footer: Footer & { footerMenu: FooterMenu };
	redirects: Redirect[];
	language: Language;
	translatedSlugs?: {
		selected: boolean;
		lang: Language;
		name: string;
		path: string;
	}[];
}

export interface Redirect extends StoryblokContent {
	fromUrl: string;
	status: string;
	toUrl: string;
}

export interface Banner extends ISbComponent {
	content: StoryblokRichTextNode<string>;
	variant: 'accent' | 'error' | 'info' | 'success' | 'warning';
}

export type FooterMenu = {
	title: string;
	items: FooterItems[];
}[];

export interface Footer extends StoryblokContent {
	menu: {
		title: string;
		items: FooterItems[];
	};
	footermenu: FooterMenu | undefined;
}

export interface FooterItems {
	title: string;
	url: string;
	target: string;
}

export interface StoryblokLink {
	id: string;
	url: string;
	linktype: string;
	fieldtype: string;
	cached_url: string;
	prep: boolean;
}

export interface StoryblokMarkdownLink {
	href: string;
	uuid: string;
	target: 'self' | 'blank';
	linktype: 'url' | 'story' | 'asset' | 'email';
}

export interface TranslatedStoryblokLink {
	id: number;
	uuid: string;
	slug: string;
	path: string;
	parent_id: number;
	name: string;
	is_folder: boolean;
	published: boolean;
	is_startpage: boolean;
	full_slug: string;
	url: string;
	real_path: string;
	position: number;
	alternates: {
		lang: string;
		name: string;
		path: string;
	}[];
}

// Needed because Storyblok doesnt export ISbLinkURLObject properly
export interface ISbStoryLinks extends ISbStory {
	data: {
		cv: number;
		story: ISbStoryData;
		stories?: ISbStoryData[];
		links: TranslatedStoryblokLink[];
		rels: ISbStoryData[];
	};
}

// Needed because Storyblok doesnt export ISbComponentType and Content properly
export interface ISbComponent {
	_uid: string;
	component: string;
	translatedSlugs: {
		selected: boolean;
		lang: Language;
		name: string;
		path: string;
	}[];
}

export interface Storytelling {
	component: string;
	bynder?: BynderImage[];
	video?: Video[];
	isFullscreen: boolean;
	extraImage: BynderImage[];
	title: string;
	overline: string;
	content: [];
	_editable: string;
	_uid: string;
}

export interface Usp {
	icon: string;
	title: string;
	description: string;
	link?: Link | string;
}

export interface UspWrapper {
	USP: Usp[];
	_editable?: string;
	component: string;
}

export interface Video {
	vimeoId: string;
	showControls: boolean;
}

export interface TranslatedSlug {
	selected: boolean;
	lang: Language;
	name: string;
	path: string;
}

export interface ItemsSliderSection {
	component: string;
	title: HeadingGroupSection[];
	items: ItemSliderItem[];
	_uid: string;
}
export interface HeadingGroupSection {
	component: string;
	_editable?: string;
	_uid?: string;
	content: string;
	headingType: HeadingType;
	overline: string;
	alignment?: 'left' | 'center';
	title: HeadingGroupSectionTitle | string;
}

export interface CardsSection {
	cards: ISbStoryBlok[];
	title: HeadingGroupSection[];
}

export interface StoryblokReference {
	_uid: string;
	type: string;
	component: 'reference';
	reference: string[];
}

interface HeadingGroupSectionTitle {
	content: {
		content: {
			text: string;
			type: string;
		}[];
		type: string;
	}[];
}
